import SpinnerIcon from '@eversports/design-tokens/assets/icons/regular/spinner.svg?icon';
import SuccessIcon from '@eversports/design-tokens/assets/icons/solid/circle-check.svg?icon';
import ErrorIcon from '@eversports/design-tokens/assets/icons/solid/error.svg?icon';
import { cn } from '@eversports/klimt-design-components/tailwind/utils';
import { InputHTMLAttributes, Ref } from 'react';
type Align = 'left' | 'center' | 'right';
type Tracking = 'normal' | 'otp';
type InputProps = Pick<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'onInput' | 'onBlur' | 'onClick' | 'onKeyDown' | 'min' | 'max' | 'step' | 'type' | 'placeholder' | 'disabled' | 'minLength' | 'maxLength' | 'name' | 'required' | 'aria-label' | 'id' | 'inputMode' | 'pattern' | 'autoComplete' | 'autoFocus'>;
interface Props extends InputProps {
  error?: string;
  errorField?: string;
  hasLengthCounter?: boolean;
  info?: string;
  isFullWidth?: boolean;
  success?: string;
  textAlign?: Align;
  tracking?: Tracking;
  ref?: Ref<HTMLInputElement>;
  dataTestId?: string;
  isLoading?: boolean;
}
const ALIGN: Record<Align, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right'
};
const TRACKING: Record<Tracking, string> = {
  normal: 'tracking-normal',
  otp: 'tracking-[0.5em] tabular-nums'
};
export const Input = (props: Props) => {
  return <div className={cn(props.isFullWidth ? 'w-full' : 'inline-block')}>
      <div className="relative">
        <input id={props.id || props.name} ref={props.ref} name={props.name} min={props.min} max={props.max} step={props.step} minLength={props.minLength} maxLength={props.maxLength} type={props.type} value={props.value} defaultValue={props.defaultValue} placeholder={props.placeholder} onChange={props.onChange} onInput={props.onInput} onClick={props.onClick} onBlur={props.onBlur} onKeyDown={props.onKeyDown} disabled={props.disabled} required={props.required} aria-label={props['aria-label']} aria-invalid={Boolean(props.error)} aria-describedby={props.error ? `${props.name}-error` : undefined} inputMode={props.inputMode} pattern={props.pattern} autoComplete={props.autoComplete} autoFocus={props.autoFocus} data-error-field={props.error ? props.errorField : undefined} data-testid={props.dataTestId} className={cn('border-border-divider bg-surface-white hover:border-border-brand-primary hover:bg-surface-primary focus:border-border-brand-primary min-h-5.5 gap-1 rounded-md py-1 pl-2 flex w-full items-center border outline-hidden', props.textAlign && ALIGN[props.textAlign], props.tracking && TRACKING[props.tracking], props.type === 'number' && !props.isFullWidth && 'max-w-10', props.error ? 'border-border-error text-text-secondary hover:border-border-error focus:border-border-error pr-4' : 'pr-1', props.disabled && 'border-border-disabled bg-surface-disabled text-text-disabled hover:border-border-disabled hover:bg-surface-disabled focus:border-border-disabled cursor-not-allowed')} />

        {props.error && <ErrorIcon className="text-text-error right-2 size-1.5 absolute top-1/2 -translate-y-1/2" aria-hidden="true" />}

        {props.success && !props.error && <SuccessIcon className="text-text-success right-2 size-1.5 absolute top-1/2 -translate-y-1/2" aria-hidden="true" />}

        {props.isLoading && <SpinnerIcon className="right-2 size-1.5 animate-spin text-text-brand-primary absolute top-1/2 -translate-y-1/2" aria-hidden="true" />}

        <div className="py-0.5 text-sm absolute top-full z-10 w-max bg-transparent">
          <div className={cn('flex w-full', props.error && 'text-text-error')}>
            <div>{props.error || props.success || props.info}</div>
            {props.hasLengthCounter && <div>
                {props.value?.toString().length}
                {props.maxLength ? '/' + props.maxLength.toString() : ''}
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
Input.displayName = 'Input';